'use client';

import { useLogger } from 'next-axiom';
import {
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { removeGid } from '../../../lib';
import { useAddToWishlist } from './use-add-to-wishlist';
import { useDefaultWishlist } from './use-default-wishlist';
import { useWishlistItems } from './use-wishlist-items';
import { useWishlistMutations } from './use-wishlist-mutations';
import { useWishlistSafe } from './use-wishlist-safe';

interface UseWishlistProductToggleProps {
  productGid: string;
  variantGid: string;
  /**
   * Strategy for adding items to wishlist
   * - 'all-items': Add to "All Items" without specific board (default)
   * - 'default-board': Add to the user's default board
   */
  addStrategy?: 'all-items' | 'default-board';
}

interface UseWishlistProductToggleReturn {
  /** Whether the product is currently in the wishlist */
  isInWishlist: boolean;
  /** Whether any wishlist operation is currently loading */
  isLoading: boolean;
  /** Whether the wishlist system is ready for operations */
  isReady: boolean;
  /** Handler for toggling wishlist state */
  handleToggle: (e: MouseEvent) => void;
  /** Direct handler for adding to wishlist */
  handleAdd: () => Promise<void>;
  /** Direct handler for removing from wishlist (requires existing item) */
  handleRemove: (itemId: string) => void;
  /** The existing wishlist item if found */
  existingItem: { id: string; productId: string; variantId: string } | null;
}

/**
 * Consolidated hook for wishlist product toggle functionality.
 * Handles all business logic including optimistic updates, error handling,
 * and different add strategies (all-items vs default-board).
 */
export function useWishlistProductToggle({
  productGid,
  variantGid,
  addStrategy = 'all-items',
}: UseWishlistProductToggleProps): UseWishlistProductToggleReturn {
  const wishlistContext = useWishlistSafe();
  const { isReady } = useDefaultWishlist();
  const { data: allItems } = useWishlistItems();
  const [optimisticState, setOptimisticState] = useState<boolean | null>(null);
  const { removeItemMutation, addItemMutation } = useWishlistMutations();
  const { addToWishlist, isLoading: isAddingToWishlist } = useAddToWishlist();
  const { defaultList } = useDefaultWishlist();

  const log = useLogger().with({
    location: 'useWishlistProductToggle',
    productGid,
    variantGid,
    addStrategy,
  });

  // Convert GIDs to numeric IDs for Swish API
  const productId = useMemo(() => Number(removeGid(productGid)), [productGid]);
  const variantId = useMemo(() => Number(removeGid(variantGid)), [variantGid]);

  // Check if item is in any wishlist using the same data source as All Items view
  const actualIsInWishlist = useMemo(() => {
    if (!allItems) return false;
    return allItems.some(
      (item) =>
        Number(item.productId) === productId &&
        Number(item.variantId) === variantId
    );
  }, [allItems, productId, variantId]);

  // Use optimistic state if available, otherwise use actual state
  const isInWishlist =
    optimisticState !== null ? optimisticState : actualIsInWishlist;

  // Clear optimistic state when actual state matches
  useEffect(() => {
    if (optimisticState !== null && optimisticState === actualIsInWishlist) {
      setOptimisticState(null);
    }
  }, [optimisticState, actualIsInWishlist]);

  // Find the existing item if it exists
  const existingItem = useMemo(() => {
    if (!allItems) return null;
    const found = allItems.find(
      (item) =>
        Number(item.productId) === productId &&
        Number(item.variantId) === variantId
    );
    if (!found) return null;
    return {
      id: found.id,
      productId: String(found.productId),
      variantId: String(found.variantId),
    };
  }, [allItems, productId, variantId]);

  // Handle optimistic updates for adding items
  const handleAdd = useCallback(async () => {
    // Optimistic update
    setOptimisticState(true);

    try {
      if (addStrategy === 'all-items') {
        // Use the useAddToWishlist hook for "All Items" strategy
        await addToWishlist({
          productId,
          variantId,
          quantity: 1,
        });
      } else if (addStrategy === 'default-board') {
        // Use addItemMutation directly for default board strategy
        if (!defaultList) {
          throw new Error('Default list not available');
        }

        await new Promise<void>((resolve, reject) => {
          addItemMutation.mutate(
            {
              listId: defaultList.id,
              productId,
              variantId,
              quantity: 1,
            },
            {
              onSuccess: () => resolve(),
              onError: (error) => reject(error),
            }
          );
        });
      }
    } catch (error) {
      // Revert optimistic state
      setOptimisticState(null);
      log.error('Add item failed', {
        error,
        productId,
        variantId,
        addStrategy,
      });
      throw error; // Re-throw for caller handling
    }
  }, [
    productId,
    variantId,
    addStrategy,
    addToWishlist,
    addItemMutation,
    defaultList,
    log,
  ]);

  const handleRemove = useCallback(
    (itemId: string) => {
      // Optimistic update
      setOptimisticState(false);

      removeItemMutation.mutate(itemId, {
        onError: (error) => {
          // Revert optimistic state
          setOptimisticState(null);
          log.error('Remove item failed', { error, itemId });
        },
      });
    },
    [removeItemMutation, log]
  );

  const handleToggle = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const isLoading =
        isAddingToWishlist ||
        removeItemMutation.isPending ||
        addItemMutation.isPending;

      if (isLoading || !isReady) return;

      // For default-board strategy, also check if defaultList is available
      if (addStrategy === 'default-board' && !defaultList) return;

      if (isInWishlist && existingItem) {
        handleRemove(existingItem.id);
      } else {
        handleAdd().catch(() => {
          // Error already logged in handleAdd
        });
      }
    },
    [
      isAddingToWishlist,
      removeItemMutation.isPending,
      addItemMutation.isPending,
      isReady,
      addStrategy,
      defaultList,
      isInWishlist,
      existingItem,
      handleRemove,
      handleAdd,
    ]
  );

  // Calculate loading state
  const isLoading = useMemo(() => {
    return (
      isAddingToWishlist ||
      removeItemMutation.isPending ||
      addItemMutation.isPending ||
      !wishlistContext
    );
  }, [
    isAddingToWishlist,
    removeItemMutation.isPending,
    addItemMutation.isPending,
    wishlistContext,
  ]);

  return {
    isInWishlist,
    isLoading,
    isReady: isReady && !!wishlistContext,
    handleToggle,
    handleAdd,
    handleRemove,
    existingItem,
  };
}
